import { Component, computed, inject, input } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';
import { startOfToday, subDays } from 'date-fns';
import { INTEREST_TYPE } from 'src/app/services/cases/cases.service';
import { SzamlazzhuInvoiceGroup } from 'src/app/services/szamlazzhu/types';
import { ClaimType } from 'src/types';
import {
  AddClaimEvidenceDialogComponent,
  DialogData,
} from '../add-claim-evidence-dialog/add-claim-evidence-dialog.component';
import {
  CASE_TYPE,
  CaseType,
  ClaimControls,
  ClaimFileControls,
  EditCaseDialogService,
} from '../edit-case-dialog.service';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { MatError } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeDropzoneComponent } from '../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeMiniIconButtonComponent } from '../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeDatepickerComponent } from '../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeDropdownComponent } from '../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { DatePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-claim-form',
  templateUrl: './claim-form.component.html',
  styleUrls: ['./claim-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    MatError,
    MatProgressSpinner,
    PayeeDropzoneComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeDatepickerComponent,
    PayeeInputDirective,
    PayeeDropdownComponent,
    DatePipe,
    MatTooltip,
  ],
})
export class ClaimFormComponent {
  private editCaseDialogService = inject(EditCaseDialogService);
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);

  readonly form = input.required<FormGroup<ClaimControls>>();
  readonly index = input.required<number>();
  readonly globalCaseType = input.required<FormControl<CaseType>>();
  readonly claimTypes = input.required<ClaimType[]>();
  readonly currencyOptions = input.required<string[]>();
  readonly szamlazzhuInvoices = input.required<SzamlazzhuInvoiceGroup[]>();
  readonly claimTypeOptions = computed<number[]>(() =>
    this.claimTypes().map((ct) => ct.id),
  );
  readonly szamlazzhuInvoiceOptions = computed<string[]>(() =>
    this.szamlazzhuInvoices().map((i) => i.invoice.invoice_id),
  );

  readonly CASE_TYPE = CASE_TYPE;
  readonly INTEREST_TYPE = INTEREST_TYPE;
  readonly max = subDays(startOfToday(), 1);

  invoiceOcrLoading = false;

  get isSzamlazzhu(): boolean {
    return !!this.editCaseDialogService.data?.payee_case.is_szamlazzhu;
  }

  get value() {
    return this.form().value;
  }
  get id() {
    return this.form().controls.id;
  }
  get ref_id() {
    return this.form().controls.ref_id;
  }
  get files() {
    return this.form().controls.files;
  }
  get due_date_at() {
    return this.form().controls.due_date_at;
  }
  get remaining_amount() {
    return this.form().controls.remaining_amount;
  }
  get original_amount() {
    return this.form().controls.original_amount;
  }
  get currency_iso() {
    return this.form().controls.currency_iso;
  }
  get type_id() {
    return this.form().controls.type_id;
  }
  get specification() {
    return this.form().controls.specification;
  }
  get interest_claim() {
    return this.form().controls.interest_claim;
  }
  get interest_claim_remaining_amount() {
    return this.interest_claim.controls.remaining_amount;
  }
  get interest_claim_original_amount() {
    return this.interest_claim.controls.original_amount;
  }
  get interest_rate() {
    return this.interest_claim.controls.interest_rate;
  }

  isAiFilled(control: AbstractControl | null | undefined): boolean {
    return this.editCaseDialogService.isAiFilled(control);
  }

  hasAiFilledIn(root: AbstractControl | null | undefined): boolean {
    return this.editCaseDialogService.hasAiFilledIn(root);
  }

  remove(): void {
    const claims = this.editCaseDialogService.form.controls.claims;
    claims.removeAt(this.index());
  }

  minDate(): string | Date | null {
    return this.value.files?.[0]?.invoice_date || null;
  }

  async claimEvidencesDropped(files: FileList): Promise<void> {
    if (files.length === 0) {
      return;
    }

    const file = files.item(0)!;
    const fileForm = this.editCaseDialogService.addClaimFile(this.files, file);

    this.invoiceOcrLoading = true;
    let ocrSucceeded = false;

    try {
      const [ocrData, legalRelationshipOcrData, condominiumOcrData] =
        await this.runClaimEvidenceOcrs(file);
      this.editCaseDialogService.applyInvoiceOcrToClaim(
        this.form(),
        fileForm,
        ocrData,
        this.claimTypes(),
      );
      ocrSucceeded = true;

      if (
        condominiumOcrData &&
        (condominiumOcrData.is_condominium_account_statement ||
          this.editCaseDialogService.isCondominiumCommonCostClaimType(
            this.type_id.value,
            this.claimTypes(),
          ))
      ) {
        this.editCaseDialogService.applyCondominiumAccountStatementOcr(
          fileForm,
          this.globalCaseType(),
          condominiumOcrData,
        );
      }

      if (
        legalRelationshipOcrData &&
        this.hasLegalRelationshipOcrResult(legalRelationshipOcrData)
      ) {
        this.applyLegalRelationshipFromOcr(
          legalRelationshipOcrData,
          fileForm,
        );
      }
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Invoice OCR error', error.response?.data);
        this.snackbar.open(
          (error.response?.data as { message?: string })?.message ??
            'A számla felismerése sikertelen.',
          'OK',
          { duration: 5000 },
        );
      } else {
        console.error('Invoice OCR error', error);
        this.snackbar.open('A számla felismerése sikertelen.', 'OK', {
          duration: 5000,
        });
      }
    } finally {
      this.invoiceOcrLoading = false;
    }

    if (!ocrSucceeded) {
      const data: DialogData = {
        claimForm: this.form(),
        fileForm,
      };
      this.dialog.open(AddClaimEvidenceDialogComponent, {
        data,
      });
    }
  }

  private async runClaimEvidenceOcrs(
    file: File,
  ): Promise<
    [
      Awaited<ReturnType<EditCaseDialogService['getInvoiceOcr']>>,
      Awaited<
        ReturnType<EditCaseDialogService['getLegalRelationshipOcr']>
      > | null,
      Awaited<
        ReturnType<
          EditCaseDialogService['getCondominiumAccountStatementOcr']
        >
      > | null,
    ]
  > {
    return Promise.all([
      this.editCaseDialogService.getInvoiceOcr(file),
      this.fetchLegalRelationshipOcr(file),
      this.globalCaseType().value === CASE_TYPE.CONDOMINIUM
        ? this.fetchCondominiumAccountStatementOcr(file)
        : Promise.resolve(null),
    ]);
  }

  private async fetchLegalRelationshipOcr(
    file: File,
  ): Promise<Awaited<
    ReturnType<EditCaseDialogService['getLegalRelationshipOcr']>
  > | null> {
    try {
      return await this.editCaseDialogService.getLegalRelationshipOcr(file);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Legal relationship OCR error', error.response?.data);
      } else {
        console.error('Legal relationship OCR error', error);
      }

      return null;
    }
  }

  private async fetchCondominiumAccountStatementOcr(
    file: File,
  ): Promise<Awaited<
    ReturnType<EditCaseDialogService['getCondominiumAccountStatementOcr']>
  > | null> {
    try {
      return await this.editCaseDialogService.getCondominiumAccountStatementOcr(
        file,
      );
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error(
          'Condominium account statement OCR error',
          error.response?.data,
        );
      } else {
        console.error('Condominium account statement OCR error', error);
      }

      return null;
    }
  }

  private hasLegalRelationshipOcrResult(
    data: Awaited<ReturnType<EditCaseDialogService['getLegalRelationshipOcr']>>,
  ): boolean {
    return !!(
      data?.legal_relationship_id ||
      data?.invoice_item ||
      data?.claim_type_internal_id ||
      data?.case_type ||
      data?.contract_date_at ||
      data?.contract_identifier ||
      data?.claim_identification_text
    );
  }

  private applyLegalRelationshipFromOcr(
    legalRelationshipOcrData: NonNullable<
      Awaited<ReturnType<EditCaseDialogService['getLegalRelationshipOcr']>>
    >,
    fileForm: FormGroup<ClaimFileControls>,
  ): void {
    this.editCaseDialogService.applyLegalRelationshipOcr(
      legalRelationshipOcrData,
      {
        claimForm: this.form(),
        fileForm,
      },
    );
  }

  removeEvidence(fileIndex: number): void {
    this.files.removeAt(fileIndex);
  }

  editClaimFile(file: AbstractControl): void {
    if (file instanceof FormGroup) {
      const data: DialogData = {
        claimForm: this.form(),
        fileForm: file as FormGroup<ClaimFileControls>,
      };
      this.dialog.open(AddClaimEvidenceDialogComponent, {
        data,
      });
    }
  }

  openClaimFile(file: AbstractControl): void {
    const value = (file as FormGroup<ClaimFileControls>).getRawValue();
    const storage_url = value.storage_url;
    if (!storage_url) {
      return;
    } else if (typeof storage_url === 'string') {
      window.open(storage_url, '_blank');
    } else {
      window.open(URL.createObjectURL(storage_url), '_blank');
    }
  }

  showClaimTypeSpecification(): boolean {
    if (!this.value.type_id) {
      return false;
    }

    const claimType = this.claimTypes().find(
      (ct) => ct.id === this.value.type_id,
    );
    return claimType?.internal_id === '99-a';
  }

  readonly szamlazzhuInvoiceDisplayWith = (invoice_id?: string): string => {
    if (!invoice_id) {
      return '';
    }
    return (
      this.szamlazzhuInvoices().find(
        (group) => group.invoice.invoice_id === invoice_id,
      )?.invoice?.invoice_number ?? ''
    );
  };

  readonly claimTypeDisplayedWith = (id: number): string => {
    return this.claimTypes().find((ct) => ct.id === id)?.label || '';
  };
}
